import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect, useMemo } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import TeamLeadFilter from "@/components/TeamLeadFilter";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Download, Upload, Pencil, Trash2, Users2, Loader2, UserPlus, KeyRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const DEPTS = ["Medical Coding", "Home Health Medical Coding", "Engineering", "Human Resources", "Business Development", "Finance", "IT Operations", "Sales"];
const DESIGNATIONS = [
  "Trainee Medical Coder",
  "Medical Coder",
  "Quality Analyst",
  "Senior Medical Coder",
  "Team Lead",
  "Process Coach",
  "Manager",
  "HR",
  "HR Manager",
  "CEO",
  "Director",
  "Admin Manager",
];

export default function Employees() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [refresh, setRefresh] = useState(0);
  const [tlFilter, setTlFilter] = useState("_all_");
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const csvRef = useRef(null);
  const [form, setForm] = useState({
    name: "", email: "", employee_id: "", department: "Engineering",
    designation: "", status: "active", location: "Bengaluru", joining_date: "", salary: 0,
    team_lead_id: "", reporting_manager_id: "",
  });

  const canCreate = ["super_admin", "hr", "manager"].includes(user?.role);
  const canDelete = ["super_admin", "hr", "manager"].includes(user?.role);
  const canSetReporting = ["super_admin", "hr", "manager"].includes(user?.role);
  const canManageUsers = ["super_admin", "hr"].includes(user?.role);
  // Reset-password is a broader privilege: Admin / HR / Manager / TL (scope enforced on backend).
  const canResetPassword = ["super_admin", "hr", "manager", "team_lead"].includes(user?.role);
  // Salary visibility (and editing) restricted to Admin / HR / Manager per HR policy.
  const canSeeSalary = ["super_admin", "hr", "manager"].includes(user?.role);
  const [allTLs, setAllTLs] = useState([]);
  const [allMgrs, setAllMgrs] = useState([]);
  const [userEmails, setUserEmails] = useState(() => new Set());

  useEffect(() => {
    if (canCreate) {
      api.get("/employees").then((r) => {
        const list = r.data || [];
        setAllTLs(list.filter((e) => /team lead|process coach/i.test(e.designation || "")));
        setAllMgrs(list.filter((e) => /manager|admin manager|ceo|director/i.test(e.designation || "")));
      });
    }
    if (canManageUsers || canResetPassword) {
      api.get("/users").then((r) => {
        const emails = new Set((r.data || []).map((u) => (u.email || "").toLowerCase()).filter(Boolean));
        setUserEmails(emails);
      }).catch(() => {});
    }
  }, [canCreate, canManageUsers, canResetPassword, refresh]);

  const createUserAccount = async (row) => {
    if (!row?.email) return toast.error("Employee needs an email before creating an account");
    if (!window.confirm(`Create a user account for ${row.name}?\nThey will sign in with email + default password Solemnity@123 and be forced to change it.`)) return;
    try {
      const { data } = await api.post("/users", {
        email: row.email,
        name: row.name,
        role: ((row.designation || "").toLowerCase().includes("hr") ? "hr"
          : (row.designation || "").toLowerCase() === "ceo" ? "super_admin"
          : /manager|director/i.test(row.designation || "") ? "manager"
          : /team lead|process coach/i.test(row.designation || "") ? "team_lead"
          : "employee"),
        employee_id: row.employee_id,
        mobile: row.mobile || row.phone || "",
      });
      toast.success(`Account created — default password Solemnity@123 sent to ${data.email}`);
      setUserEmails((prev) => new Set([...prev, (data.email || "").toLowerCase()]));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetUserPassword = async (row) => {
    if (!row?.email) return toast.error("No email on file");
    if (!window.confirm(`Reset ${row.name}'s password to Solemnity@123?\nThey'll be forced to change it on next login.`)) return;
    try {
      const { data: users } = await api.get("/users");
      const u = (users || []).find((x) => (x.email || "").toLowerCase() === row.email.toLowerCase());
      if (!u) return toast.error("No user account exists yet — create one first");
      await api.post(`/users/${u.id}/reset-password`);
      toast.success(`${row.name}'s password reset to Solemnity@123`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Reporting dialog state
  const [reportingOpen, setReportingOpen] = useState(false);
  const [reportingFor, setReportingFor] = useState(null);
  const [reportingForm, setReportingForm] = useState({ team_lead_emp_id: "", reporting_manager_emp_id: "" });
  const [allEmployees, setAllEmployees] = useState([]);
  const [savingReporting, setSavingReporting] = useState(false);

  const openReporting = async (row) => {
    setReportingFor(row);
    setReportingForm({
      team_lead_emp_id: row.team_lead_id || "_none_",
      reporting_manager_emp_id: row.reporting_manager_id || "_none_",
    });
    setReportingOpen(true);
    if (allEmployees.length === 0) {
      const r = await api.get("/employees");
      setAllEmployees(r.data || []);
    }
  };

  const saveReporting = async () => {
    setSavingReporting(true);
    try {
      const payload = {
        team_lead_emp_id: reportingForm.team_lead_emp_id === "_none_" ? null : reportingForm.team_lead_emp_id,
        reporting_manager_emp_id: reportingForm.reporting_manager_emp_id === "_none_" ? null : reportingForm.reporting_manager_emp_id,
      };
      await api.post(`/employees/${reportingFor.id}/reporting`, payload);
      toast.success("Reporting updated");
      setReportingOpen(false);
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSavingReporting(false); }
  };

  // Memoized lists for the reporting dropdowns — avoids re-filtering on every render
  const tlOptions = useMemo(() => (
    allEmployees.filter((e) => e.id !== reportingFor?.id && /team lead|process coach/i.test(e.designation || ""))
  ), [allEmployees, reportingFor]);

  const mgrOptions = useMemo(() => (
    allEmployees.filter((e) => e.id !== reportingFor?.id && /manager|admin manager|ceo|director/i.test(e.designation || ""))
  ), [allEmployees, reportingFor]);

  const deleteEmployee = async (row) => {
    if (!window.confirm(`Delete ${row.name} (${row.employee_id})? This action cannot be undone.`)) return;
    try {
      await api.delete(`/employees/${row.id}`);
      toast.success(`${row.name} deleted`);
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const submit = async () => {
    try {
      // Resolve TL and Manager names from IDs before posting
      const tlObj = form.team_lead_id ? allTLs.find((e) => e.employee_id === form.team_lead_id) : null;
      const mgrObj = form.reporting_manager_id ? allMgrs.find((e) => e.employee_id === form.reporting_manager_id) : null;
      const payload = {
        ...form,
        team_lead_name: tlObj?.name || "",
        reporting_manager_name: mgrObj?.name || "",
      };
      await api.post("/employees", payload);
      toast.success("Employee added");
      setOpen(false);
      setForm({
        name: "", email: "", employee_id: "", department: "Engineering",
        designation: "", status: "active", location: "Bengaluru", joining_date: "", salary: 0,
        team_lead_id: "", reporting_manager_id: "",
      });
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add employee");
    }
  };

  const startEdit = (row) => {
    setEditing({
      ...row,
      salary: row.salary || 0,
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      const { id, ...rest } = editing;
      await api.put(`/employees/${id}`, rest);
      toast.success("Employee updated");
      setEditOpen(false);
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/employees/bulk-import", fd);
      toast.success(`Imported ${data.inserted}, skipped ${data.skipped}`);
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  const downloadSampleCsv = () => {
    const csv = "name,email,employee_id,department,designation,status,joining_date,location,salary,manager,phone\nJane Doe,jane@solemnity.com,EMP-0099,Engineering,Senior Engineer,active,2026-03-01,Bengaluru,1800000,Marcus Vale,+91-90000-11111\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "employees_sample.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // Filter function passed to DataTable — re-memoized when TL filter changes.
  const tlFilterFn = useMemo(
    () => (r) => !tlFilter || tlFilter === "_all_" || r.team_lead_id === tlFilter,
    [tlFilter]
  );

  return (
    <div data-testid="employees-page">
      <PageHeader
        title="Employees"
        subtitle="Centralized directory of everyone in your organization."
        action={
          <div className="flex gap-2">
            <Button data-testid="employees-sample-csv" variant="outline" className="h-9" onClick={downloadSampleCsv}>
              <Download className="h-4 w-4 mr-1.5" /> Sample CSV
            </Button>
            {canCreate && (
              <>
                <label className="inline-flex items-center h-9 px-3 rounded-md border border-gray-300 hover:bg-gray-50 cursor-pointer text-sm" data-testid="employees-csv-import-label">
                  <Upload className="h-4 w-4 mr-1.5" /> Import CSV
                  <input ref={csvRef} data-testid="employees-csv-input" type="file" accept=".csv" className="hidden" onChange={importCsv} />
                </label>
                <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="employees-add-button" className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
                    <Plus className="h-4 w-4 mr-1.5" /> Add employee
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add employee</DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Full name" testId="emp-name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                    <Field label="Email" testId="emp-email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                    <Field label="Employee ID" testId="emp-id"><Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} /></Field>
                    <Field label="Designation" testId="emp-desig">
                      <Select value={form.designation} onValueChange={(v) => setForm({ ...form, designation: v })}>
                        <SelectTrigger data-testid="emp-desig-select"><SelectValue placeholder="Select designation" /></SelectTrigger>
                        <SelectContent>
                          {DESIGNATIONS.map((d) => (
                            <SelectItem key={d} value={d} data-testid={`emp-desig-option-${d.replace(/\s+/g, "-").toLowerCase()}`}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Department" testId="emp-dept">
                      <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="Joining date" testId="emp-joining"><Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} /></Field>
                    <Field label="Location" testId="emp-loc"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
                    {canSeeSalary && (
                      <Field label="Annual salary (INR)" testId="emp-salary"><Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: Number(e.target.value) })} /></Field>
                    )}
                    <Field label="Reporting Team Lead" testId="emp-tl">
                      <Select value={form.team_lead_id || "_none_"} onValueChange={(v) => setForm({ ...form, team_lead_id: v === "_none_" ? "" : v })}>
                        <SelectTrigger data-testid="emp-tl-select"><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="_none_">— None —</SelectItem>
                          {allTLs.map((e) => (
                            <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`emp-tl-${e.employee_id}`}>
                              {e.name} · <span className="text-gray-400 font-mono text-xs">{e.employee_id}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Reporting Manager" testId="emp-mgr">
                      <Select value={form.reporting_manager_id || "_none_"} onValueChange={(v) => setForm({ ...form, reporting_manager_id: v === "_none_" ? "" : v })}>
                        <SelectTrigger data-testid="emp-mgr-select"><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="_none_">— None —</SelectItem>
                          {allMgrs.map((e) => (
                            <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`emp-mgr-${e.employee_id}`}>
                              {e.name} · <span className="text-gray-400 font-mono text-xs">{e.employee_id}</span> · {e.designation}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                <DialogFooter>
                    <Button data-testid="emp-create-submit" onClick={submit} className="bg-[#F26B3A] hover:bg-[#d85d2f]">Add employee</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </>
            )}
          </div>
        }
      />

      <DataTable
        testId="employees-table"
        endpoint="/employees"
        refreshKey={refresh}
        onRowClick={(row) => navigate(`/employees/${row.id}`)}
        searchPlaceholder="Search by name, email, ID, dept…"
        toolbar={(
          <TeamLeadFilter value={tlFilter} onChange={setTlFilter} testIdPrefix="employees-tl-filter" />
        )}
        filterFn={tlFilterFn}
        columns={[
          { key: "employee_id", label: "ID", mono: true },
          { key: "name", label: "Name", render: (r) => (
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-[#1E88E5] text-white grid place-items-center text-[11px] font-semibold">
                {(r.name || "U").split(" ").map((s) => s[0]).slice(0,2).join("")}
              </div>
              <div>
                <div className="font-medium text-gray-800">{r.name}</div>
                <div className="text-[11px] text-gray-500">{r.email}</div>
              </div>
            </div>
          )},
          { key: "designation", label: "Designation" },
          { key: "department", label: "Department" },
          { key: "reporting", label: "Reports to", render: (r) => (
            <div className="text-xs text-gray-600 leading-tight">
              {r.team_lead_name && <div>TL: <span className="text-gray-800">{r.team_lead_name}</span></div>}
              {r.reporting_manager_name && <div>Mgr: <span className="text-gray-800">{r.reporting_manager_name}</span></div>}
              {!r.team_lead_name && !r.reporting_manager_name && <span className="text-gray-400">—</span>}
            </div>
          )},
          { key: "location", label: "Location" },
          { key: "status", label: "Status", badge: true },
          ...(canCreate || canDelete || canSetReporting || canManageUsers || canResetPassword ? [{ key: "actions", label: "", render: (r) => (
            <div className="flex gap-0.5">
              {canSetReporting && (
                <Button
                  data-testid={`reporting-emp-${r.id}`}
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); openReporting(r); }}
                  className="h-7 w-7 p-0 text-[#1E88E5]"
                  title="Set reporting"
                >
                  <Users2 className="h-3.5 w-3.5" />
                </Button>
              )}
              {canManageUsers && r.email && !userEmails.has(r.email.toLowerCase()) && (
                <Button
                  data-testid={`create-account-${r.id}`}
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); createUserAccount(r); }}
                  className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50"
                  title="Create user account (default password: Solemnity@123)"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                </Button>
              )}
              {canResetPassword && r.email && userEmails.has(r.email.toLowerCase()) && (
                <Button
                  data-testid={`reset-pwd-${r.id}`}
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); resetUserPassword(r); }}
                  className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50"
                  title="Reset password to Solemnity@123"
                >
                  <KeyRound className="h-3.5 w-3.5" />
                </Button>
              )}
              {canCreate && (
                <Button
                  data-testid={`edit-emp-${r.id}`}
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); startEdit(r); }}
                  className="h-7 w-7 p-0"
                  title="Edit"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {canDelete && (
                <Button
                  data-testid={`delete-emp-${r.id}`}
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); deleteEmployee(r); }}
                  className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}] : []),
        ]}
      />

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit employee</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name" testId="edit-emp-name"><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
              <Field label="Email" testId="edit-emp-email"><Input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></Field>
              <Field label="Designation" testId="edit-emp-desig">
                <Select value={editing.designation || ""} onValueChange={(v) => setEditing({ ...editing, designation: v })}>
                  <SelectTrigger data-testid="edit-emp-desig-select"><SelectValue placeholder="Select designation" /></SelectTrigger>
                  <SelectContent>
                    {DESIGNATIONS.map((d) => (
                      <SelectItem key={d} value={d} data-testid={`edit-emp-desig-option-${d.replace(/\s+/g, "-").toLowerCase()}`}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Department" testId="edit-emp-dept">
                <Select value={editing.department || "Engineering"} onValueChange={(v) => setEditing({ ...editing, department: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Status" testId="edit-emp-status">
                <Select value={editing.status || "active"} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["active", "probation", "on_leave", "inactive"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Location" testId="edit-emp-loc"><Input value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></Field>
              <Field label="Manager" testId="edit-emp-mgr"><Input value={editing.manager || ""} onChange={(e) => setEditing({ ...editing, manager: e.target.value })} /></Field>
              {canSeeSalary && (
                <Field label="Annual salary (INR)" testId="edit-emp-salary"><Input type="number" value={editing.salary || 0} onChange={(e) => setEditing({ ...editing, salary: Number(e.target.value) })} /></Field>
              )}
              <Field label="Reporting Team Lead" testId="edit-emp-tl">
                <Select value={editing.team_lead_id || "_none_"} onValueChange={(v) => {
                  if (v === "_none_") setEditing({ ...editing, team_lead_id: "", team_lead_name: "" });
                  else { const t = allTLs.find((e) => e.employee_id === v); setEditing({ ...editing, team_lead_id: v, team_lead_name: t?.name || "" }); }
                }}>
                  <SelectTrigger data-testid="edit-emp-tl-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="_none_">— None —</SelectItem>
                    {allTLs.map((e) => (
                      <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`edit-emp-tl-${e.employee_id}`}>
                        {e.name} · <span className="text-gray-400 font-mono text-xs">{e.employee_id}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Reporting Manager" testId="edit-emp-rmgr">
                <Select value={editing.reporting_manager_id || "_none_"} onValueChange={(v) => {
                  if (v === "_none_") setEditing({ ...editing, reporting_manager_id: "", reporting_manager_name: "" });
                  else { const m = allMgrs.find((e) => e.employee_id === v); setEditing({ ...editing, reporting_manager_id: v, reporting_manager_name: m?.name || "" }); }
                }}>
                  <SelectTrigger data-testid="edit-emp-rmgr-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="_none_">— None —</SelectItem>
                    {allMgrs.map((e) => (
                      <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`edit-emp-rmgr-${e.employee_id}`}>
                        {e.name} · <span className="text-gray-400 font-mono text-xs">{e.employee_id}</span> · {e.designation}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="edit-emp-submit" onClick={submitEdit} className="bg-[#F26B3A] hover:bg-[#d85d2f]">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Reporting dialog */}
      <Dialog open={reportingOpen} onOpenChange={setReportingOpen}>
        <DialogContent className="max-w-lg" data-testid="reporting-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users2 className="h-5 w-5 text-[#1E88E5]" /> Set reporting — {reportingFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-sky-800">
              Hierarchy: <b>Trainee / Coder → Team Lead → Manager → CEO</b>. HR / Manager / Admin can override at any time.
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Reporting Team Lead</Label>
              <Select value={reportingForm.team_lead_emp_id} onValueChange={(v) => setReportingForm({ ...reportingForm, team_lead_emp_id: v })}>
                <SelectTrigger data-testid="reporting-tl-select" className="mt-1.5">
                  <SelectValue placeholder="Select Team Lead" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="_none_" data-testid="reporting-tl-none">— None —</SelectItem>
                  {tlOptions.map((e) => (
                    <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`reporting-tl-${e.employee_id}`}>
                      {e.name} · <span className="text-gray-400 font-mono text-xs">{e.employee_id}</span> · {e.designation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Reporting Manager</Label>
              <Select value={reportingForm.reporting_manager_emp_id} onValueChange={(v) => setReportingForm({ ...reportingForm, reporting_manager_emp_id: v })}>
                <SelectTrigger data-testid="reporting-mgr-select" className="mt-1.5">
                  <SelectValue placeholder="Select Manager / CEO" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="_none_" data-testid="reporting-mgr-none">— None —</SelectItem>
                  {mgrOptions.map((e) => (
                    <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`reporting-mgr-${e.employee_id}`}>
                      {e.name} · <span className="text-gray-400 font-mono text-xs">{e.employee_id}</span> · {e.designation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportingOpen(false)}>Cancel</Button>
            <Button data-testid="reporting-save" onClick={saveReporting} disabled={savingReporting} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
              {savingReporting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <Label className="text-xs uppercase tracking-wider text-gray-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
